import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import Horse from "@/components/icons/Horse";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import SearchBox from "@/components/SearchBox";
import EmptyState from "@/components/EmptyState";
import PhotoUploader from "@/components/PhotoUploader";
import { useCrudList, crudCreate, crudUpdate, crudDelete } from "@/lib/crud";

const empty = {
  nome: "", raca: "", idade: "", sexo: "", altura: "", peso: "",
  temperamento: "", nivel_treino: "", restricoes_txt: "",
  ativo: true, observacoes: "",
};

export default function Cavalos() {
  const [q, setQ] = useState("");
  const { items, loading, setItems } = useCrudList("/cavalos", { q });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...empty, ...c,
      restricoes_txt: (c.restricoes || []).join(", "),
      idade: c.idade ?? "", altura: c.altura ?? "", peso: c.peso ?? "",
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      payload.restricoes = (form.restricoes_txt || "").split(",").map((s) => s.trim()).filter(Boolean);
      delete payload.restricoes_txt;
      ["idade", "altura", "peso"].forEach((k) => {
        if (payload[k] === "") delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      });
      if (editing) {
        const upd = await crudUpdate("/cavalos", editing.id, payload);
        setItems((prev) => prev.map((x) => x.id === upd.id ? upd : x));
        toast.success("Cavalo atualizado");
      } else {
        const c = await crudCreate("/cavalos", payload);
        setItems((prev) => [c, ...prev]);
        setEditing(c);
        toast.success("Cavalo cadastrado");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await crudDelete("/cavalos", deleteId);
      setItems((prev) => prev.filter((x) => x.id !== deleteId));
      toast.success("Cavalo excluído");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setDeleteId(null); }
  };

  return (
    <div data-testid="cavalos-page">
      <PageHeader
        icon={Horse}
        title="Cavalos"
        description="Ficha completa dos cavalos utilizados nas sessões."
        actions={<Button onClick={openNew} data-testid="cavalo-new-btn"><Plus className="h-4 w-4 mr-1" />Novo cavalo</Button>}
      />

      <div className="mb-4"><SearchBox value={q} onChange={setQ} placeholder="Buscar por nome, raça, temperamento…" testId="cavalos-search" /></div>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Carregando…</div>
          ) : items.length === 0 ? (
            <div className="p-6"><EmptyState icon={Horse} title="Nenhum cavalo" description="Cadastre os cavalos que atuam na equoterapia." action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1" />Novo cavalo</Button>} /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nome</TableHead><TableHead>Raça</TableHead>
                <TableHead>Idade</TableHead><TableHead>Temperamento</TableHead>
                <TableHead>Status</TableHead><TableHead className="w-24"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`cavalo-row-${c.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        {c.foto_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${c.foto_url}`} alt="" className="h-9 w-9 rounded-full object-cover border border-border" />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center"><Horse className="h-4 w-4 text-primary" /></div>
                        )}
                        {c.nome}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{c.raca || "—"}</TableCell>
                    <TableCell>{c.idade ? `${c.idade} anos` : "—"}</TableCell>
                    <TableCell>{c.temperamento || "—"}</TableCell>
                    <TableCell><Badge variant={c.ativo ? "secondary" : "outline"}>{c.ativo ? "Ativo" : "Inativo"}</Badge></TableCell>
                    <TableCell><div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar cavalo" : "Novo cavalo"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {editing && (
              <PhotoUploader entity="cavalos" entityId={editing.id} currentUrl={editing.foto_url}
                onUploaded={(url) => {
                  setEditing({ ...editing, foto_url: url });
                  setItems((prev) => prev.map((x) => x.id === editing.id ? { ...x, foto_url: url } : x));
                }} />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Nome" req><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="cavalo-form-nome" /></F>
              <F label="Raça"><Input value={form.raca || ""} onChange={(e) => setForm({ ...form, raca: e.target.value })} /></F>
              <F label="Idade (anos)"><Input type="number" value={form.idade} onChange={(e) => setForm({ ...form, idade: e.target.value })} /></F>
              <F label="Sexo">
                <Select value={form.sexo || ""} onValueChange={(v) => setForm({ ...form, sexo: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent><SelectItem value="macho">Macho</SelectItem><SelectItem value="femea">Fêmea</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="Altura (m)"><Input type="number" step="0.01" value={form.altura} onChange={(e) => setForm({ ...form, altura: e.target.value })} /></F>
              <F label="Peso (kg)"><Input type="number" step="0.1" value={form.peso} onChange={(e) => setForm({ ...form, peso: e.target.value })} /></F>
              <F label="Temperamento">
                <Select value={form.temperamento || ""} onValueChange={(v) => setForm({ ...form, temperamento: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="calmo">Calmo</SelectItem>
                    <SelectItem value="docil">Dócil</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="reativo">Reativo</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Nível de treino">
                <Select value={form.nivel_treino || ""} onValueChange={(v) => setForm({ ...form, nivel_treino: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basico">Básico</SelectItem>
                    <SelectItem value="intermediario">Intermediário</SelectItem>
                    <SelectItem value="avancado">Avançado</SelectItem>
                  </SelectContent>
                </Select>
              </F>
            </div>
            <F label="Restrições (separadas por vírgula)"><Input value={form.restricoes_txt} placeholder="Peso máx 60kg, sem cavaleiros iniciantes…" onChange={(e) => setForm({ ...form, restricoes_txt: e.target.value })} /></F>
            <F label="Observações"><Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></F>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div><p className="text-sm font-medium">Ativo</p><p className="text-xs text-muted-foreground">Aparece na agenda</p></div>
              <Switch checked={!!form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
              <Button type="submit" disabled={saving} data-testid="cavalo-form-submit" className="bg-primary hover:bg-[#3B704E]">{saving ? "Salvando…" : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir cavalo?</AlertDialogTitle><AlertDialogDescription>Ação irreversível.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function F({ label, req, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>{children}</div>);
}
